import { useAuthStore } from '@/stores/auth'
import { useApiError } from '@/composables/api/useApiError'
import { apiClient } from '@/api/client'
import { extractData } from '@/api/responseExtractors'
import type { CorpUserSegmentation } from '@/types';

const PATH = '/manager/me'

/** KYC da empresa aprovado na Swap; aguardando Individual KYC / ativação final. */
export const BUSINESS_UNIT_KYC_APPROVED_STATUS = 'business_kyc_approved' as const

/** Status da unidade de negócio (conta) retornado pelo GET /manager/me. */
export type BusinessUnitStatus =
  | 'incomplete'
  | 'in_analysis'
  | typeof BUSINESS_UNIT_KYC_APPROVED_STATUS
  | 'approved'
  | 'rejected'
  | string

/** Empresa enviada ao KYC ou aguardando ativação final — usuário deve ficar na tela de espera. */
export function isAwaitingAccountActivation(status: BusinessUnitStatus | undefined): boolean {
  return status === 'in_analysis' || status === BUSINESS_UNIT_KYC_APPROVED_STATUS
}

/** Status do cadastro de sócio retornado pelo GET /manager/me. */
export type ShareholderRegistrationStatus = 'incomplete' | 'data_valid' | 'ready' | string

/** Conta/gestor retornada em data.account pelo GET /manager/me. */
export interface ManagerMeAccount {
  id?: string
  name?: string
  email?: string
  cnpj?: string | null
  /** Razão social da empresa da conta atual (identifica a empresa, não o gestor). */
  company_name?: string | null
  [key: string]: unknown
}

/**
 * Contato pessoal (canal ECX) do titular, retornado em `data.person` pelo GET /manager/me.
 * Fonte de verdade no Sessions; aqui é a leitura (pull-on-read) para a tela de perfil.
 */
export interface ManagerMePerson {
  document?: string
  email?: string | null
  phone?: string | null
  email_verified?: boolean | null
  phone_verified?: boolean | null
}

/**
 * Permissão RBAC no formato `resource:action` (ex.: `users:index`).
 * Super admin: resposta pode conter apenas `*` no array.
 */
export type ManagerPermission = string

/** Perfil customizado vinculado à conta (exibir nome na UI). */
export interface ManagerCustomRole {
  id: string
  name: string
  kind?: 'super_admin' | 'admin' | 'custom' | 'user'
}

/**
 * Resposta de sucesso do GET /manager/me (envelope: `data` achatado).
 * Ver RBAC: permissões espelham Pundit (`has_permission?("cards","index")` → `cards:index`).
 */
export interface ManagerMeData {
  /** Identificador do subject da sessão (JWT). */
  id?: string
  /** Papel da sessão na API (ex.: `web`). */
  role?: string
  terms_accepted?: boolean
  /** Há versão obrigatória pendente de aceite (nova versão ou nunca aceito). */
  terms_pending?: boolean
  /** Rótulo da versão obrigatória vigente na API. */
  current_mandatory_version?: string | null
  /** Última versão obrigatória aceita nesta conta (web), se houver histórico. */
  accepted_mandatory_version?: string | null
  /** Recorte de acesso do próprio gestor logado: é o teto do que ele pode atribuir a outro. */
  segmentation?: CorpUserSegmentation
  account_status?: string
  business_unit_status?: BusinessUnitStatus
  /** Status do negócio (pode ser rejected independente de business_unit_status). */
  business_status?: string
  shareholder_registration_status?: ShareholderRegistrationStatus
  /** Lista ordenada de `resource:action` ou `["*"]` para super admin. */
  permissions?: ManagerPermission[]
  custom_role?: ManagerCustomRole | null
  /** Dados do gestor (nome, email para toolbar). */
  account?: ManagerMeAccount
  /** Contato pessoal (canal ECX) do titular — CPF, email, telefone e estado validado. */
  person?: ManagerMePerson
  links?: Record<string, unknown>
  [key: string]: unknown
}

export type GetMeResult =
  | { ok: true; data: ManagerMeData }
  | { ok: false; code: string; message?: string }

/**
 * GET /manager/me — estado **somente da conta** enviada em `X-ACCOUNT-ID` (sem lista de contas).
 * `terms_accepted`, `business_unit_status`, `account`, permissões etc. refletem essa conta;
 * trocar o header muda o payload. Com JWT + conta válidos costuma ser 200; o caller trata `terms_accepted` no body.
 * Usa apiClient com skipTermsRedirect para não redirecionar em 403 de fluxo legado.
 */
export function useManagerMeApi() {
  const authStore = useAuthStore()

  const getMe = async (): Promise<GetMeResult> => {
    if (!authStore.hasAccountSelected) {
      return { ok: false, code: 'account_required', message: 'Conta não selecionada' }
    }
    try {
      const envelope = await apiClient.get(PATH, { skipTermsRedirect: true })
      const data = extractData(envelope.data) as ManagerMeData | undefined
      return { ok: true, data: data ?? {} }
    } catch (err) {
      const api = useApiError(err)
      if (api.getStatus() === 403) {
        const code = api.getPrimaryErrorCode()
        return {
          ok: false,
          code: code ?? 'forbidden',
          message: api.getMessage(),
        }
      }
      return { ok: false, code: 'error', message: api.getMessage() }
    }
  }

  /** Indica se o status da unidade exige tela "Validar dados" (incomplete ou in_analysis). */
  const needsValidateData = (status: BusinessUnitStatus | undefined): boolean => {
    if (!status) return true
    return status === 'incomplete' || status === 'in_analysis'
  }

  /** Indica se o cadastro foi reprovado (validação nos attributes do /me, igual in-analysis). */
  const isRejected = (data: ManagerMeData | undefined): boolean => {
    if (!data) return false
    return data.business_unit_status === 'rejected' || data.business_status === 'rejected'
  }

  /**
   * BU ou business em `data_valid` sem ambos `approved`: GET /manager/me segue 200, mas o restante
   * da API Manager exige aprovação — enviar para conferência (onboarding step 2 / validate-data).
   */
  const needsPreApprovalDataConference = (data: ManagerMeData | undefined): boolean => {
    if (!data) return false
    const unit = data.business_unit_status
    const biz = data.business_status
    if (unit === 'approved' && biz === 'approved') return false
    return unit === 'data_valid' || biz === 'data_valid'
  }

  /**
   * Indica se o cadastro de sócio está incompleto e exige tela "Completar dados do sócio".
   * Retorna true quando status é 'incomplete' ou null/undefined (representante existe mas
   * registration_status nunca foi calculado, ou unidade ainda sem representante legal).
   * Quando a unidade já está **approved**, não exige fluxo de sócios (empresa já aprovada).
   */
  const needsCompleteShareholder = (
    shareholderStatus: ShareholderRegistrationStatus | undefined | null,
    businessUnitStatus?: BusinessUnitStatus
  ): boolean => {
    if (businessUnitStatus === 'approved') return false
    if (isAwaitingAccountActivation(businessUnitStatus)) return false
    return shareholderStatus !== 'data_valid' && shareholderStatus !== 'ready'
  }

  return {
    getMe,
    needsValidateData,
    needsPreApprovalDataConference,
    needsCompleteShareholder,
    isRejected,
    isAwaitingAccountActivation,
  }
}
